import { useEffect, useState } from 'react'

const STORAGE_KEY = 'pauline:auth:next_resend'

/**
 * Сохраняет ISO-время ближайшего resend в sessionStorage (или очищает, если null).
 * @param isoDate - ISO-время или null для очистки
 */
export const saveNextResend = (isoDate: string | null) => {
  if (!isoDate) sessionStorage.removeItem(STORAGE_KEY)
  else sessionStorage.setItem(STORAGE_KEY, isoDate)
}

/**
 * Читает ISO-время ближайшего resend из sessionStorage.
 * @returns ISO-строка или null
 */
export const readNextResend = (): string | null => {
  return sessionStorage.getItem(STORAGE_KEY)
}

/**
 * Парсит ISO-строку, трактуя отсутствие `Z` или offset как UTC.
 * Бэк отдаёт `2026-04-16T11:54:35.889819` без суффикса — без этого
 * `new Date(...)` интерпретирует как локальное время и таймер считает неверно.
 */
const parseIsoAsUtc = (iso: string): number => {
  const hasTz = /[zZ]|[+-]\d{2}:?\d{2}$/.test(iso)
  return new Date(hasTz ? iso : `${iso}Z`).getTime()
}

const computeLeft = (targetIso: string | null): number => {
  if (!targetIso) return 0
  const target = parseIsoAsUtc(targetIso)
  if (Number.isNaN(target)) return 0
  const diff = target - Date.now()
  return diff <= 0 ? 0 : Math.ceil(diff / 1000)
}

/**
 * Секунд до ISO-таймстемпа (UTC-aware).
 * @param iso - ISO-строка или null
 * @returns Секунд или 0 если в прошлом/null
 */
export const secondsUntil = (iso: string | null | undefined): number => {
  return computeLeft(iso ?? null)
}

/**
 * Форматирует сообщение о доступности повторной отправки.
 * @param iso - ISO-таймстемп next_resend
 * @returns "Повторная отправка через N сек" либо null если время истекло
 */
export const formatResendWaitMessage = (iso: string | null | undefined): string | null => {
  const left = secondsUntil(iso)
  if (left <= 0) return null
  return `Повторная отправка доступна через ${left} сек.`
}

/**
 * Хук-обратный отсчёт до ISO-таймстемпа. Тикает каждую секунду.
 * @param targetIso - Целевое ISO-время (или null)
 * @returns Секунд до targetIso (0 если истёк/не задан)
 */
export const useResendCountdown = (targetIso: string | null): number => {
  const [secondsLeft, setSecondsLeft] = useState(() => computeLeft(targetIso))

  useEffect(() => {
    setSecondsLeft(computeLeft(targetIso))
    if (!targetIso) return
    const id = window.setInterval(() => {
      setSecondsLeft(computeLeft(targetIso))
    }, 1000)
    return () => window.clearInterval(id)
  }, [targetIso])

  return secondsLeft
}
